import React, { useEffect, useState } from 'react';
import { Inputs, DEFAULT_INPUTS } from '../utils/roiCalculations';
import { ResultsPage } from './ResultsPage';
export function PreviewEditor() {
  const [inputs, setInputs] = useState<Inputs>(DEFAULT_INPUTS);
  const [showPreview, setShowPreview] = useState(true);
  // Load initial inputs from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('previewInputs');
    if (stored) {
      try {
        setInputs(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse preview inputs', e);
      }
    }
  }, []);
  // Save inputs to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('previewInputs', JSON.stringify(inputs));
  }, [inputs]);
  const handleChange = (field: keyof Inputs, value: any) => {
    setInputs({
      ...inputs,
      [field]: value
    });
  };
  const handleNumericChange = (field: keyof Inputs, value: string) => {
    if (value === '') {
      setInputs({
        ...inputs,
        [field]: ''
      });
      return;
    }
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setInputs({
        ...inputs,
        [field]: numValue
      });
    }
  };
  return <div className="min-h-screen bg-gray-100" data-id="element-566">
      {/* Split View */}
      <div className="flex h-screen" data-id="element-567">
        {/* Editor Panel - Left Side */}
        <div className="w-96 bg-white border-r border-gray-300 overflow-y-auto" data-id="element-568">
          <div className="p-6 border-b border-gray-200 bg-navy text-white sticky top-0 z-10" data-id="element-569">
            <h1 className="text-xl font-bold" data-id="element-570">Preview Editor</h1>
            <p className="text-sm text-blue-200 mt-1" data-id="element-571">
              Changes sync to /results in real-time
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-572">
            {/* Company Info */}
            <div data-id="element-573">
              <h3 className="font-bold text-navy mb-3" data-id="element-574">Company Info</h3>
              <div className="space-y-3" data-id="element-575">
                <div data-id="element-576">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-577">
                    Company Name
                  </label>
                  <input type="text" value={inputs.companyName} onChange={e => handleChange('companyName', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-578" />
                </div>
                <div data-id="element-579">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-580">
                    Email
                  </label>
                  <input type="email" value={inputs.email} onChange={e => handleChange('email', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-581" />
                </div>
              </div>
            </div>

            {/* Recruiting Budget */}
            <div data-id="element-582">
              <h3 className="font-bold text-navy mb-3" data-id="element-583">Recruiting Budget</h3>
              <div className="space-y-3" data-id="element-584">
                <div data-id="element-585">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-586">
                    Monthly Ad Spend ($)
                  </label>
                  <input type="number" min="0" step="500" value={inputs.adSpend} onChange={e => handleNumericChange('adSpend', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-587" />
                </div>
                <div data-id="element-588">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-589">
                    Cost Per Hire ($)
                  </label>
                  <input type="number" min="1" step="50" value={inputs.cph} onChange={e => handleNumericChange('cph', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-590" />
                </div>
                <div data-id="element-591">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-592">
                    Recruiter Hourly Rate ($)
                  </label>
                  <input type="number" min="0" step="5" value={inputs.recruiterHourlyRate} onChange={e => handleNumericChange('recruiterHourlyRate', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-593" />
                </div>
              </div>
            </div>

            {/* Revenue & Margins */}
            <div data-id="element-594">
              <h3 className="font-bold text-navy mb-3" data-id="element-595">Revenue & Margins</h3>
              <div className="space-y-3" data-id="element-596">
                <div data-id="element-597">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-598">
                    Average Deal Size ($)
                  </label>
                  <input type="number" min="0" step="1000" value={inputs.ticket} onChange={e => handleNumericChange('ticket', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-599" />
                </div>
                <div data-id="element-600">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-601">
                    Gross Profit Margin (%)
                  </label>
                  <input type="number" min="0" max="100" step="1" value={inputs.gpm} onChange={e => handleNumericChange('gpm', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-602" />
                </div>
              </div>
            </div>

            {/* Sales Performance */}
            <div data-id="element-603">
              <h3 className="font-bold text-navy mb-3" data-id="element-604">Sales Performance</h3>
              <div className="space-y-3" data-id="element-605">
                <div data-id="element-606">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-607">
                    Sales in First 90 Days (Total)
                  </label>
                  <input type="number" min="0" step="1" value={inputs.salesFirst90Total} onChange={e => handleNumericChange('salesFirst90Total', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-608" />
                </div>
                <div data-id="element-609">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-610">
                    Monthly Sales (Veteran Reps)
                  </label>
                  <input type="number" min="0" step="1" value={inputs.salesVetMonthly} onChange={e => handleNumericChange('salesVetMonthly', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-611" />
                </div>
                <div data-id="element-612">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-613">
                    Deal Close Rate (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pullthrough} onChange={e => handleNumericChange('pullthrough', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-614" />
                </div>
              </div>
            </div>

            {/* Retention Rates */}
            <div data-id="element-615">
              <h3 className="font-bold text-navy mb-3" data-id="element-616">Retention Rates</h3>
              <div className="space-y-3" data-id="element-617">
                <div data-id="element-618">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-619">
                    Hired to Launched (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pctLaunched} onChange={e => handleNumericChange('pctLaunched', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-620" />
                </div>
                <div data-id="element-621">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-622">
                    Launched to Day 30 (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct30} onChange={e => handleNumericChange('pct30', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-623" />
                </div>
                <div data-id="element-624">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-625">
                    Day 30 to Day 60 (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct60} onChange={e => handleNumericChange('pct60', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-626" />
                </div>
                <div data-id="element-627">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-628">
                    Day 60 to Day 90 (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct90} onChange={e => handleNumericChange('pct90', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-629" />
                </div>
                <div data-id="element-630">
                  <label className="block text-xs font-semibold text-gray-700 mb-1" data-id="element-631">
                    Day 90 to 1 Year (%)
                  </label>
                  <input type="number" min="0" max="100" step="5" value={inputs.pct1y} onChange={e => handleNumericChange('pct1y', e.target.value)} className="w-full p-2 rounded border border-gray-300 text-sm" data-id="element-632" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Panel - Right Side */}
        <div className="flex-1 overflow-y-auto bg-gray-50" data-id="element-633">
          <div className="sticky top-0 z-10 bg-white border-b border-gray-300 px-6 py-3 flex items-center justify-between" data-id="element-634">
            <div className="flex items-center gap-2" data-id="element-635">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse" data-id="element-636"></div>
              <span className="text-sm font-semibold text-gray-700" data-id="element-637">
                Live Preview
              </span>
            </div>
            <a href="/results" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 font-semibold" data-id="element-638">
              Open in New Tab →
            </a>
          </div>
          <ResultsPage isPreview={true} data-id="element-639" />
        </div>
      </div>
    </div>;
}